import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Grid, Stack, CircularProgress } from '@mui/material';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import Empty from 'components/empty';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import {
    OrderPayload,
    isAppUrlValid,
    isDestUrlValid,
    isRecipientUrlValid,
    ssoOktaDialogPopup,
    isRolesValid,
    isServiceProviderSigningCertValid,
    uploadedFilesState,
} from 'pages/sso-okta/store';
import { formOrderObjFromOrderDetails, formUploadObjFromOrderDetails, resetOrder } from 'pages/sso-okta/utils';
import SsoOktaStyles from 'pages/sso-okta/style';
import Roles from 'pages/sso-okta/components/Dialog-box/Roles';
import axios from 'api/request';
import logger from 'utils/logger';

const SsoOktaHeader = React.lazy(() => import('pages/sso-okta/ssoOktaHeader'));
const CommonForm = React.lazy(() => import('pages/sso-okta/CommonForm'));

const ssoOkta = () => {
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [, setAppUrlValid] = useRecoilState(isAppUrlValid);
    const [, setDestUrlValid] = useRecoilState(isDestUrlValid);
    const [, setRecipientUrlValid] = useRecoilState(isRecipientUrlValid);
    const [, setServiceProviderSigningCertValid] = useRecoilState(isServiceProviderSigningCertValid);
    const [, setRolesValid] = useRecoilState(isRolesValid);
    const [, setDialogPopup] = useRecoilState(ssoOktaDialogPopup);
    const [, setUploadObj] = useRecoilState(uploadedFilesState);
    const [isLoading, setLoading] = React.useState(false);
    const [logoSrc, setLogoSrc] = React.useState(null);

    // const axios = useAxios();
    const handleReset = () => {
        setOrder(resetOrder);
        setAppUrlValid(true);
        setDestUrlValid(true);
        setRecipientUrlValid(true);
        setServiceProviderSigningCertValid(true);
        setDialogPopup(false);
        setUploadObj([]);
    };

    // Helper to convert ArrayBuffer to base64
    function arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        return window.btoa(binary);
    }
    const logoMutation = useMutation((payload) => axios.post(`${endPoints.ssoOkta.applicationLogo}`, payload));

    async function checkCatalog() {
        setLoading(true);
        try {
            // const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
            if (cartItemId && operation && catalogServiceId) {
                const { data: orderDetails } = await axios.get(`${endPoints.riServices.Jwt.orderDetails}/${cartItemId}`);
                const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
                const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
                const logoArray = [];
                if (uploadObj?.length > 0 && uploadObj[0]?.id && uploadObj[0]?.blobRelativeLink) {
                    try {
                        const logoResponse = await logoMutation.mutateAsync({
                            id: uploadObj[0]?.id,
                            blobRelativeLink: uploadObj[0]?.blobRelativeLink,
                        });

                        let base64Logo = null;
                        if (logoResponse instanceof Blob) {
                            // If it's a Blob, convert to ArrayBuffer first
                            const arrayBuffer = await logoResponse.arrayBuffer();
                            base64Logo = arrayBufferToBase64(arrayBuffer);
                        } else if (logoResponse instanceof ArrayBuffer) {
                            base64Logo = arrayBufferToBase64(logoResponse);
                        } else if (logoResponse?.data && logoResponse?.data instanceof ArrayBuffer) {
                            base64Logo = arrayBufferToBase64(logoResponse?.data);
                        }

                        if (base64Logo) {
                            const logoDataUrl = `data:image/png;base64,${base64Logo}`;
                            setLogoSrc(logoDataUrl);
                            uploadObj[0].href = logoDataUrl;
                            logoArray.push(uploadObj[0]);
                        }
                    } catch (err) {
                        logger.error('Error fetching applicationLogoApi:', err);
                    }
                }
                const response = await axios.get(`${endPoints?.ssoOkta?.applicationOwner(orderObj?.businessApplication)}`);
                const updatedOrderObj = {
                    ...orderObj,
                    appOwnerDetails: {
                        name: response?.data?.owner,
                        gpid: response?.data?.gpId,
                        email: response?.data?.email,
                    },
                    appOwnerText: `${response?.data?.gpId} - ${response?.data?.owner}` || null,
                };
                setUploadObj(logoSrc ? logoArray : uploadObj);
                setAppUrlValid(true);
                setDestUrlValid(true);
                setRecipientUrlValid(true);
                setServiceProviderSigningCertValid(true);
                setRolesValid(true);
                setOrder(() => ({
                    ...order,
                    ...updatedOrderObj,
                }));
            } else {
                setOrder((previous) => ({
                    ...previous,
                    isTouched: false,
                }));
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (catalogCode) {
            handleReset();
            checkCatalog();
        }
    }, [catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => handleReset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>SSO Integration</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={SsoOktaStyles.ssoOkta_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={SsoOktaStyles.circularProgressColor} />
                    </Box>
                }
            >
                {isLoading && (
                    <Stack sx={SsoOktaStyles?.emptyStack}>
                        <Empty isLoading color="#000000" msg="Loading..." />
                    </Stack>
                )}
                {!isLoading && (
                    <>
                        <SsoOktaHeader reset={() => handleReset()} />
                        <Grid item container spacing={2}>
                            <Grid item xs={12}>
                                <Grid display="flex" flexDirection="column" padding={4} gap={3} className="contentContainer">
                                    <CommonForm />
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
                <Roles />
            </Suspense>
        </>
    );
};

export default ssoOkta;
